import { DateTime } from "luxon";

import {
  IInboxContextDonor,
  IInboxContextOrder,
  IInboxContextPart,
  IInboxConversationRow,
  IInboxPartContext,
  IInboxThread,
} from "../models/messagesInbox/IInboxApi";
import { IInboxConversation, IInboxMessage, IInboxOrder, IInboxPart, IInboxVehicle } from "../models/messagesInbox/IInboxConversation";

/** Reply-by is 24 h after the buyer's last message, as on the sample data. */
export const REPLY_WITHIN_HOURS = 24;

const orUndefined = <T>(value: T | null | undefined): T | undefined => (value === null ? undefined : value);

function addHours(iso: string, hours: number) {
  const at = DateTime.fromISO(iso, { zone: "utc" });
  return at.isValid ? (at.plus({ hours }).toISO() as string) : iso;
}

/**
 * A row from GET conversations. Messages, part, donor and order come later (thread + part context).
 * Reply-by runs from the buyer's oldest unanswered message (waitingSince), so a buyer who chases
 * keeps their place in the queue; an older API without it falls back to their newest message.
 */
export function conversationFromRow(row: IInboxConversationRow, replyWindowHours = REPLY_WITHIN_HOURS): IInboxConversation {
  const waitingSince = row.waitingSince ?? undefined;
  return {
    id: row.id,
    channel: row.channel,
    buyer: { username: row.buyerUsername, fullName: orUndefined(row.buyerName) },
    summary: row.lastMessagePreview || row.subject || row.itemTitle || "",
    status: row.noReplyNeeded ? "no_reply_needed" : row.needsReply ? "needs_reply" : "waiting_on_buyer",
    // AI's reading of what the buyer wants, where the API triages (the "Messages AI" flag); "other" until then.
    type: row.type ?? "other",
    typeConfidence: orUndefined(row.typeConfidence),
    aiSummary: orUndefined(row.aiSummary),
    missing: row.missing && row.missing.length > 0 ? row.missing : undefined,
    newAddress: row.newAddress && row.newAddress.length > 0 ? row.newAddress : undefined,
    buyerVehicleDescription: orUndefined(row.buyerVehicle),
    unread: row.unread,
    // An API that can't store the mark leaves the field out, and the button stays hidden.
    wilmaStatusSupported: row.noReplyNeeded !== undefined,
    hasNotes: !!row.hasNotes,
    lastBuyerMessageAt: row.lastBuyerMessageAt,
    replyBy: addHours(waitingSince ?? row.lastBuyerMessageAt, replyWindowHours),
    waitingSince,
    waitingMessages: row.waitingMessages && row.waitingMessages > 0 ? row.waitingMessages : undefined,
    regRequestSentAt: row.autoRequestKind === "reg" ? orUndefined(row.autoRequestSentAt) : undefined,
    lastMessageAt: row.lastMessageAt,
    // Not needing a reply means the last word was ours (eBay: answered; PartPro: last message from us).
    // A conversation marked no-reply-needed wasn't replied to, so it never shows as Sent.
    lastSentAt: row.needsReply || row.noReplyNeeded ? undefined : row.lastMessageAt,
    partId: orUndefined(row.partId),
    subject: row.subject || undefined,
    itemTitle: orUndefined(row.itemTitle),
    ebayItemId: orUndefined(row.ebayItemId),
    partTagNumber: orUndefined(row.partTagNumber),
    vehicleVrm: orUndefined(row.vehicleVrm),
    orderNumber: orUndefined(row.orderNumber),
    messages: [],
  };
}

/**
 * A fresh list row over what we already had for that conversation: the thread, part and order
 * stay until they reload, and a conversation opened here stays read until something new arrives.
 */
export function mergeRow(fresh: IInboxConversation, previous: IInboxConversation | undefined): IInboxConversation {
  if (!previous) return fresh;
  const nothingNew = previous.lastMessageAt === fresh.lastMessageAt;
  return {
    ...fresh,
    unread: nothingNew && !previous.unread ? false : fresh.unread,
    orderNumber: fresh.orderNumber ?? previous.orderNumber,
    messages: previous.messages,
    part: previous.part,
    donor: previous.donor,
    order: previous.order,
  };
}

export function messagesFromThread(thread: IInboxThread): IInboxMessage[] {
  return thread.messages.map((m) => ({
    id: m.id,
    direction: m.direction === "us" ? "us" : m.direction === "note" ? "note" : m.direction === "auto" ? "auto" : "buyer",
    body: m.body,
    sentAt: m.sentAt,
    authorName: m.authorName || undefined,
    attachments: m.attachments && m.attachments.length > 0 ? m.attachments.map((a) => ({ name: a.name, url: a.url })) : undefined,
  }));
}

const normaliseBody = (body: string) => body.replace(/\s+/g, " ").trim().toLowerCase();

/** How far before the send the channel's copy of a reply may be stamped (clock drift) and still be the same reply. */
const PENDING_MATCH_MS = 5 * 60 * 1000;

/**
 * Has the thread caught up with this pending reply? Same text isn't enough: the same reply sent to
 * the buyer last week would make today's vanish. The thread's copy also has to be stamped at or
 * after the send, give or take a few minutes.
 */
function hasArrived(messages: IInboxMessage[], p: IInboxMessage): boolean {
  const body = normaliseBody(p.body);
  const sentAt = Date.parse(p.sentAt);
  return messages.some((m) => {
    if (m.direction !== "us" || normaliseBody(m.body) !== body) return false;
    const at = Date.parse(m.sentAt);
    return Number.isNaN(at) || Number.isNaN(sentAt) || at >= sentAt - PENDING_MATCH_MS;
  });
}

/**
 * Replies that really went but aren't back in the thread from eBay / PartPro yet, so a reply
 * doesn't vanish between sending and the channel catching up. Once the thread has one of ours
 * with the same text from around the same time, the pending copy goes.
 */
export function mergePendingReplies(messages: IInboxMessage[], pending: IInboxMessage[]): IInboxMessage[] {
  const waiting = pending.filter((p) => !hasArrived(messages, p));
  return [...messages, ...waiting];
}

/** Which pending replies the thread has now caught up with (so they can be forgotten). */
export function arrivedPendingIds(messages: IInboxMessage[], pending: IInboxMessage[]): string[] {
  return pending.filter((p) => hasArrived(messages, p)).map((p) => p.id);
}

/** The part's PartStatus name as the panel's stock chip. */
export function stockStatusFromPartStatus(status: string | null | undefined): IInboxPart["status"] {
  const s = (status ?? "").replace(/[\s_]/g, "").toLowerCase();
  if (s === "sold" || s === "deleted") return "Sold";
  if (s === "onthecar" || s === "pendingdismantle" || s === "notaccessible") return "On the car";
  if (s === "damaged" || s === "missing") return "Damaged";
  return "In stock";
}

export function partFromContext(p: IInboxContextPart): IInboxPart {
  return {
    id: p.id,
    title: p.title,
    shortTitle: p.title,
    price: p.price,
    condition: p.condition ?? "",
    status: stockStatusFromPartStatus(p.status),
    tagNumber: p.tagNumber ?? "",
    location: p.location ?? "",
    partNumber: orUndefined(p.partNumber),
    oePartNumber: orUndefined(p.oePartNumber),
    photoCount: p.photoCount ?? 0,
    imageUrl: orUndefined(p.imageUrl),
    categoryName: p.categoryName ?? "",
    aspects: p.aspects ?? [],
    listingUrl: orUndefined(p.ebayListingUrl),
  };
}

export function donorFromContext(d: IInboxContextDonor): IInboxVehicle {
  return {
    id: d.id,
    vrm: d.vrm ?? "",
    make: d.make ?? "",
    model: d.model ?? "",
    variant: orUndefined(d.variant),
    year: orUndefined(d.year),
    engineSize: d.engineSize === null || d.engineSize === undefined ? undefined : String(d.engineSize),
    engineCode: orUndefined(d.engineCode),
    fuel: orUndefined(d.fuel),
    gearbox: orUndefined(d.gearbox),
    colour: orUndefined(d.colour),
    mileage: orUndefined(d.mileage),
    vinLast8: orUndefined(d.vinLast8),
  };
}

export function orderFromContext(o: IInboxContextOrder): IInboxOrder {
  return {
    channel: o.channel,
    orderNumber: o.orderNumber,
    orderedAt: o.orderedAt,
    pricePaid: o.pricePaid,
    postage: o.postage,
    totalPaid: o.totalPaid,
    refundedSoFar: o.refundedSoFar,
    paymentStatus: o.paymentStatus ?? "",
    invoiceNumber: orUndefined(o.invoiceNumber),
    pickedAt: orUndefined(o.pickedAt),
    pickedBy: orUndefined(o.pickedBy),
    labelPrintedAt: orUndefined(o.labelPrintedAt),
    shippedAt: orUndefined(o.shippedAt),
    shippedBy: orUndefined(o.shippedBy),
    courier: orUndefined(o.courier),
    trackingNumber: orUndefined(o.trackingNumber),
    isCollection: o.isCollection,
    deliveryName: orUndefined(o.deliveryName),
    deliveryAddress: o.deliveryAddress ?? undefined,
    // The latest day eBay gives is the one to hold the courier to.
    estimatedDelivery: orUndefined(o.estimatedDeliveryTo ?? o.estimatedDeliveryFrom),
    estimatedDeliveryFrom: orUndefined(o.estimatedDeliveryFrom),
    estimatedDeliveryTo: orUndefined(o.estimatedDeliveryTo),
    buyerVehicleReg: orUndefined(o.buyerVehicleReg),
    canCancel: o.canCancel,
    cannotCancelReason: orUndefined(o.cannotCancelReason),
    canRefund: o.canRefund,
    cannotRefundReason: orUndefined(o.cannotRefundReason),
  };
}

/** The open conversation with its thread (plus any replies still on their way) and part context. */
export function withThreadAndContext(
  c: IInboxConversation,
  thread: IInboxThread | undefined,
  context: IInboxPartContext | undefined,
  pending: IInboxMessage[] = []
): IInboxConversation {
  const messages = mergePendingReplies(thread && thread.id === c.id ? messagesFromThread(thread) : c.messages, pending);
  if (!context || context.part.id !== c.partId) return { ...c, messages };
  return {
    ...c,
    messages,
    part: partFromContext(context.part),
    donor: context.donor ? donorFromContext(context.donor) : undefined,
    order: context.order ? orderFromContext(context.order) : undefined,
    orderNote: context.orderNote ?? undefined,
  };
}
